'use client'

import { I18nProvider } from '@/lib/i18n/I18nProvider.client'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import {
  PageNavigationLoaderMarkup,
  PageNavigationProvider,
  useRouter,
} from '@/lib/navigation/PageNavigation.client'
import { Button } from '@/ui/buttons/button/Button.client'
import { Icon } from '@/ui/icons/icon/Icon'
import { Container } from '@/ui/layouts/container/Container'
import { Logo } from '@/ui/logos/logo/Logo.client'
import { Skin } from '@/ui/skin/Skin'
import { Title } from '@/ui/text/title/Title'
import { cloneElement } from 'react'

function GoHomeButton() {
  const { pushWithProgressBar } = useRouter()

  const { t } = useTranslation(['ErrorPageHero'])

  return (
    <Button
      variant="secondary"
      size="lg"
      iconLeft={<Icon.ArrowLeft />}
      className="place-self-start"
      onPress={() => pushWithProgressBar('/')}
    >
      {t('ErrorPageHero:buttons.goHome.title')}
    </Button>
  )
}

export type ErrorPageProps = {
  skin: 'body' | 'body-alt'
  preTitle?: React.ReactNode
  title: React.ReactNode
  paragraphs: [React.ReactNode, ...React.ReactNode[]] // at least one line
  image: React.ReactElement<{ className?: string }>
  hideGoHomeButton?: boolean
  headerOutlet?: React.ReactNode
  infosOutlet?: React.ReactNode
  footerOutlet?: React.ReactNode
  actionsOutlet?: React.ReactNode
}

export function ErrorPageHero({
  skin,
  preTitle,
  title,
  paragraphs,
  image,
  hideGoHomeButton,
  headerOutlet,
  infosOutlet,
  footerOutlet,
  actionsOutlet,
}: ErrorPageProps) {
  const { t } = useTranslation(['ErrorPageHero'])

  return (
    <>
      <PageNavigationLoaderMarkup />
      <PageNavigationProvider>
        {headerOutlet || (
          <header className="fixed left-0 right-0 top-0 z-[1] grid h-8 place-items-center bg-white shadow-xl">
            <Logo size="s" />
          </header>
        )}
        <Skin
          tint={skin}
          className={
            headerOutlet
              ? 'pt-[--app-header-height] desktop:pt-[--app-header-height-desktop]'
              : 'pt-8'
          }
        >
          <Container size="md" className="grid min-h-[75vh] place-items-center gap-0">
            <div className="flex flex-col gap-4 px-1 py-3 tablet:px-2 desktop:flex-row">
              <div className="flex w-full flex-col gap-3">
                <Title size="6xl" className="flex flex-col font-bold">
                  {preTitle !== undefined ? (
                    preTitle && <span className="leading-light">{preTitle}</span>
                  ) : (
                    <span className="leading-light">{t('ErrorPageHero:title')}</span>
                  )}
                  <span className="text-5xl leading-normal">{title}</span>
                </Title>

                {!!paragraphs.length && (
                  <div>
                    {paragraphs.map((paragraph, index) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <p key={index} className="select-none leading-light">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                )}

                <div className="flex flex-wrap gap-3">
                  {!hideGoHomeButton && (
                    <I18nProvider>
                      <GoHomeButton />
                    </I18nProvider>
                  )}

                  {actionsOutlet}
                </div>

                {infosOutlet}
              </div>

              {cloneElement(image, {
                className: 'object-contain w-[18rem] tablet:w-[26rem]',
              })}
            </div>
          </Container>
          {footerOutlet}
        </Skin>
      </PageNavigationProvider>
    </>
  )
}
